import type { CellContext, ColumnDef } from "@tanstack/react-table"

import AssociateTestTemplateRowActions from "../components/AssociatedTestTemplatesTable/AssociatedTestTemplateRowActions"
import { formatAssociatedTestTemplateLimits } from "../utils"

import { DraggableCell } from "@/components/Tables/Aeros/components/DraggableCell"
import { PermissionActionType } from "@/constants/permissions"
import { type AssociatedTestTemplate } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import {
  getTestTemplateComparisonLabel,
  getTestTemplateResultUomLabel,
} from "@/screens/ManageProducts/components/TestTemplates/utils"
import { booleanToYesNoString } from "@/utils/utils"

export const useAssociatedTestTemplateColumns = (onRefresh: () => void): ColumnDef<AssociatedTestTemplate>[] => {
  const { hasPermissions } = usePermissionManager()

  return [
    ...(hasPermissions([PermissionActionType.TEST_TEMPLATE_MAINTENANCE])
      ? [
          {
            id: "drag",
            header: "",
            size: 30,
            cell: (context: CellContext<AssociatedTestTemplate, unknown>) => {
              const { row } = context
              const listeners = (context as any).listeners
              return <DraggableCell listeners={listeners} row={row} />
            },
          },
        ]
      : []),
    {
      accessorKey: "sequenceNumber",
      header: "Seq.",
      enableSorting: false,
      size: 100,
      cell: ({ row }) => row.original.sequenceNumber,
    },
    {
      accessorKey: "testNumber",
      header: "Test #",
      enableSorting: false,
      size: 100,
      cell: ({ row }) => row.original.testTemplate?.externalSystemId,
    },
    {
      accessorKey: "description",
      header: "Description",
      enableSorting: false,
      size: 300,
      cell: ({ row }) => row.original.testTemplate?.description,
    },
    {
      accessorKey: "resultsUnitOfMeasure",
      header: "Results UoM",
      enableSorting: false,
      cell: ({ row }) => getTestTemplateResultUomLabel(row.original.testTemplate?.resultsUnitOfMeasure),
    },
    {
      accessorKey: "comparison",
      header: "Comparison",
      enableSorting: false,
      cell: ({ row }) => getTestTemplateComparisonLabel(row.original.testTemplate?.comparison ?? ""),
    },

    {
      accessorKey: "limits",
      header: "Limit(s)",
      enableSorting: false,
      cell: ({ row }) => formatAssociatedTestTemplateLimits(row.original),
      size: 150,
    },
    {
      accessorKey: "printOnCoa",
      header: "Print on COA",
      enableSorting: false,
      cell: ({ row }) => booleanToYesNoString(row.original.printToAkrochem),
      size: 100,
    },
    ...(hasPermissions([PermissionActionType.TEST_TEMPLATE_MAINTENANCE])
      ? [
          {
            accessorKey: "actions",
            header: "",
            enableSorting: false,
            size: 72,
            cell: ({ row }) => <AssociateTestTemplateRowActions row={row} onRefresh={onRefresh} />,
          } as ColumnDef<AssociatedTestTemplate>,
        ]
      : []),
  ]
}
